import React, { useState, useEffect, FC, CSSProperties } from 'react';
import { authService } from '../utils/auth.ts';
import PersonIcon from '@mui/icons-material/Person';
import '../styles/colors.css';

interface AvatarProps {
  size?: 'small' | 'medium' | 'large';
  userID?: string; // ユーザーIDでアバター取得（API呼び出し）
  avatarUrl?: string | null; // 直接アバターURLを指定（最優先、API不要）
  showFallback?: boolean; // フォールバック表示の有無
  className?: string;
  style?: CSSProperties;
}

const Avatar: FC<AvatarProps> = React.memo(
  ({
    size = 'medium',
    userID,
    avatarUrl,
    showFallback = true,
    className,
    style,
  }) => {
    const [fetchedAvatarUrl, setFetchedAvatarUrl] = useState<string | null>(
      null
    );
    const [hasAvatar, setHasAvatar] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(false);

    // サイズのスタイル定義
    const sizeStyles = {
      small: {
        width: '24px',
        height: '24px',
        fontSize: '12px',
      },
      medium: {
        width: '32px',
        height: '32px',
        fontSize: '16px',
      },
      large: {
        width: '48px',
        height: '48px',
        fontSize: '24px',
      },
    };

    const currentSize = sizeStyles[size];

    useEffect(() => {
      // 直接avatarUrlが提供されている場合はAPIを呼ばない
      if (avatarUrl !== undefined) {
        setFetchedAvatarUrl(avatarUrl);
        setHasAvatar(!!avatarUrl);
        setIsLoading(false);
        setError(false);
        return;
      }

      const fetchAvatar = async () => {
        try {
          setIsLoading(true);
          setError(false);

          // 現在のユーザー情報を取得
          const currentUser = authService.getCurrentUser();

          // URL決定ロジック：userID指定なら他のユーザー、なければ自分のプロフィール
          let url = '/profile/avatar'; // デフォルトは自分のアバター

          if (userID) {
            // userIDが指定されている場合は他のユーザーのアバターを取得
            if (!currentUser || userID !== currentUser.id) {
              url = `/users/id/${encodeURIComponent(userID)}/avatar`;
            }
          }

          const response = await authService.authFetch(url, {
            useCache: true,
            ttl: 5 * 60 * 1000, // 5分間キャッシュ
          });

          if (!response.ok) {
            if (response.status === 404) {
              // ユーザーが見つからない場合
              setHasAvatar(false);
              setFetchedAvatarUrl(null);
            } else {
              throw new Error('Failed to fetch avatar');
            }
            return;
          }

          const data = await response.json();
          setHasAvatar(data.has_avatar);
          setFetchedAvatarUrl(data.avatar_url);
        } catch (err) {
          console.error('Avatar fetch error:', err);
          setError(true);
          setHasAvatar(false);
          setFetchedAvatarUrl(null);
        } finally {
          setIsLoading(false);
        }
      };

      fetchAvatar();
    }, [userID, avatarUrl]);

    // 画像読み込みエラー時のハンドラー
    const handleImageError = () => {
      setError(true);
      setHasAvatar(false);
    };

    // ローディング中
    if (isLoading) {
      return (
        <div
          className={className}
          style={{
            ...currentSize,
            borderRadius: '50%',
            backgroundColor: 'var(--neutral-200)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0.6,
            ...style,
          }}
        >
          <div
            style={{
              width: '60%',
              height: '60%',
              border: '2px solid var(--neutral-400)',
              borderTop: '2px solid transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
        </div>
      );
    }

    // 表示用のアバターURL決定（直接指定またはAPIから取得）
    const displayAvatarUrl =
      avatarUrl !== undefined ? avatarUrl : fetchedAvatarUrl;

    // アバター画像がある場合
    if (hasAvatar && displayAvatarUrl && !error) {
      return (
        <img
          src={displayAvatarUrl}
          alt="Avatar"
          className={className}
          style={{
            ...currentSize,
            borderRadius: '50%',
            objectFit: 'cover',
            border: '2px solid var(--neutral-300)',
            ...style,
          }}
          onError={handleImageError}
        />
      );
    }

    // フォールバック表示（アバター未設定またはエラー時）
    if (showFallback) {
      return (
        <div
          className={className}
          style={{
            ...currentSize,
            borderRadius: '50%',
            backgroundColor: 'var(--primary-100)',
            border: '2px solid var(--primary-300)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--primary-600)',
            ...style,
          }}
        >
          <PersonIcon style={{ fontSize: currentSize.fontSize }} />
        </div>
      );
    }

    // フォールバック非表示の場合は何も表示しない
    return null;
  }
);

export default Avatar;
